import {
  AppShell,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import {
  reset,
  initialize,
  updateField,
} from "../slices/form/customTextFieldTab";
import {
  SaveCustomFieldRequest,
  useGetVentQuery,
  useSaveCustomFieldMutation,
} from "../slices/api/ventsApi";
import { useEffect } from "react";
import {
  displayErrorMessage,
  displayServerErrorMessage,
  handleFetchingError,
} from "../shared/ErrorMessage";
import { IsBlockNav, SetBlockNav } from "../App";
import { useLocation } from "wouter";
import { ConfigText, getCustomFieldIndex } from "../slices/types";
import { LinkAnchorToVentHome } from "../shared/components/LinkAnchors";
import { EbTooltip } from "../shared/components/EbTooltip";
import { IconHelp } from "../shared/components/Icons";

interface Props {
  ventId: string;
  customFieldId: string;
  setTabDescription: (name: string) => void;
  setEventTabDescription: (name: string) => void;
}

const CustomFieldTextTab = (props: Props) => {
  const { ventId, customFieldId } = props;
  const [_] = useLocation();
  const { data: customField, hasChanges } = useAppSelector(
    (state: RootState) => state.customTextFieldTab,
  );
  const {
    data: vent,
    isFetching,
    isError: isErrorFetchingVent,
    error: ventError,
  } = useGetVentQuery({ id: ventId });
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (vent) {
      const customFieldIndex = getCustomFieldIndex(
        vent.customFields,
        customFieldId,
      );
      if (customFieldIndex < 0) {
        console.error("custom field not found");
        return;
      }
      const customField = vent.customFields[customFieldIndex];
      dispatch(initialize(customField));
      props.setTabDescription(customField.name);
      props.setEventTabDescription(vent.name);
    }
  }, [vent]);
  useEffect(() => {
    // During development useEffect is called twice by React.
    // We need to protect against that by returning if hasChanges didn't change
    if (IsBlockNav() === hasChanges) return;
    if (hasChanges) {
      SetBlockNav(true);
    } else {
      // Can happen if we support undo
      SetBlockNav(false);
    }
  }, [hasChanges]);

  const [saveCustomField, { isLoading: isSavingCustomField }] =
    useSaveCustomFieldMutation();

  const handleSave = async () => {
    if (customField == null || customField.configText == null) {
      console.error("customField is undefined but should not be");
      return;
    }
    if (customField.name === "") {
      displayErrorMessage("Name is required.");
      return;
    }

    const request: SaveCustomFieldRequest = {
      id: customFieldId,
      name: customField.name,
      description: customField.description,
      hidden: customField.hidden,
      configText: {
        required: customField.configText.required,
      },
      ventId,
      updateTime: vent?.updateTime!,
    };

    try {
      await saveCustomField(request).unwrap();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };
  if (isErrorFetchingVent) {
    handleFetchingError(ventError);
    return null;
  }
  if (isFetching || !vent || !customField || customField.configText == null) {
    return (
      <AppShell.Main>
        <Loader color="blue" />
      </AppShell.Main>
    );
  }

  return (
    <>
      <AppShell.Main>
        <Group justify="space-between" mb="xs" gap="0">
          <Stack gap="0">
            <Text mb="0" size="xl">
              {`Custom Field: ${customField.name} (text field)`}
            </Text>
            <LinkAnchorToVentHome ventId={ventId}>
              {vent.name}
            </LinkAnchorToVentHome>
          </Stack>
        </Group>
        <Paper mt="lg">
          This is a text field. The user will be able to enter up to 100
          characters.
        </Paper>
        <TextInput
          style={{ width: "300px" }}
          maxLength={100}
          disabled={!vent.canUserEdit}
          required
          mt="md"
          label="Name"
          value={customField.name ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "name",
                value: event.currentTarget.value,
              }),
            );
          }}
        />
        <TextInput
          style={{ width: "300px" }}
          maxLength={100}
          disabled={!vent.canUserEdit}
          mt="md"
          label="Description"
          value={customField.description ?? ""}
          onChange={(event) => {
            dispatch(
              updateField({
                field: "description",
                value: event.currentTarget.value,
              }),
            );
          }}
        />
        <Group mt="md" gap="xl" align="center">
          <Group gap="xs" align="center">
            <Switch
              disabled={!vent.canUserEdit}
              label="Hide"
              checked={customField.hidden}
              onChange={(event) =>
                dispatch(
                  updateField({
                    field: "hidden",
                    value: event.currentTarget.checked,
                  }),
                )
              }
            />
            <EbTooltip label="Hiding a field will not delete its data.">
              <span
                aria-label="Help"
                style={{ display: "inline-flex", cursor: "help" }}
              >
                <IconHelp />
              </span>
            </EbTooltip>
          </Group>

          <Group gap="xs" align="center">
            <Switch
              disabled={!vent.canUserEdit}
              label="Required"
              checked={customField.configText.required}
              onChange={(event) => {
                const ct: ConfigText = {
                  ...customField.configText,
                  required: event.currentTarget.checked,
                };
                dispatch(
                  updateField({
                    field: "configText",
                    value: ct,
                  }),
                );
              }}
            />
            <EbTooltip label="If required, guests will need to enter text in order to accept. It is not required for users who decline or are undecided. The minimum is not enforced unless a guest changes their RSVP after accepting the invitation.">
              <span
                aria-label="Help"
                style={{ display: "inline-flex", cursor: "help" }}
              >
                <IconHelp />
              </span>
            </EbTooltip>
          </Group>
        </Group>
        <div>
          <Button
            mt="xs"
            mr="md"
            onClick={() => dispatch(reset())}
            disabled={!hasChanges}
          >
            Discard Changes
          </Button>
          <Button
            mt="xs"
            disabled={!hasChanges || !vent.canUserEdit || isSavingCustomField}
            onClick={handleSave}
            rightSection={isSavingCustomField ? <Loader size={14} /> : null}
          >
            Save Custom Field
          </Button>
        </div>
      </AppShell.Main>
    </>
  );
};

export default CustomFieldTextTab;
